<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import { PhotoSizes } from '#cmui/types'
import type { PoolPhoto } from '#cmui/types'
import { getPhotoUrlBySize } from '#cmui/utils'

const props = defineProps({
    photos: {
        type: Array as PropType<PoolPhoto[]>,
        required: true
    },
    photoIndex: {
        type: Number,
        default: 0
    }
})

const emits = defineEmits(['closeModal'])

const poolStore = usePoolStore()

const currentIndex = ref(props.photoIndex)
const photo = useTemplateRef('photo')

const currentPhoto = computed<PoolPhoto | undefined>(() =>
    props.photos?.length ? props.photos[currentIndex.value] : undefined
)

const getTotalPicturesCount = computed<number>(() => props.photos?.length || 0)

const changePhoto = (direction: string) => {
    if (!photo.value) {
        return
    }

    if (direction === 'next') {
        photo.value.classList.add('photo-animation-left-out')
    } else {
        photo.value.classList.add('photo-animation-right-out')
    }

    const animationFunction = () => {
        if (!photo.value) {
            return
        }

        if (direction === 'next') {
            photo.value.classList.remove('photo-animation-left-out')

            if (currentIndex.value + 1 < props.photos?.length) {
                currentIndex.value += 1
            } else {
                currentIndex.value = 0
            }
            photo.value.classList.add('photo-animation-left-in')
            setTimeout(
                () => photo.value?.classList.remove('photo-animation-left-in'),
                500
            )
        } else {
            photo.value.classList.remove('photo-animation-right-out')

            if (currentIndex.value > 0) {
                currentIndex.value -= 1
            } else {
                currentIndex.value = props.photos?.length - 1
            }
            photo.value.classList.add('photo-animation-right-in')
            setTimeout(
                () => photo.value?.classList.remove('photo-animation-right-in'),
                500
            )
        }
    }
    photo.value.addEventListener('animationend', animationFunction, {
        once: true
    })
}
</script>

<template>
    <div
        class="z-10 relative w-screen h-screen flex justify-center items-center font-inter"
    >
        <span
            class="bg-black opacity-40 h-screen w-screen absolute"
            @click="emits('closeModal')"
        />
        <div class="w-fit z-10 max-h-screen flex flex-col items-center">
            <IconCross
                class="cursor-pointer text-[#DDDDDD] absolute right-5 top-5 size-6"
                @click="emits('closeModal')"
            />
            <p class="top-5 left-5 absolute text-[#DDDDDD] text-sm">
                {{ currentIndex + 1 }} / {{ getTotalPicturesCount }}
            </p>
            <div
                class="flex flex-row w-fit justify-center items-center max-w-3/4 mx-16"
            >
                <IconChevronLeft
                    class="cursor-pointer text-[#DDDDDD] transform absolute left-5 size-6"
                    @click="changePhoto('prev')"
                />
                <div ref="photo" class="relative size-full">
                    <NuxtImg
                        class="mx-auto relative top-0"
                        loading="lazy"
                        :src="
                            getPhotoUrlBySize(currentPhoto, PhotoSizes.STANDARD)
                        "
                        :alt="poolStore.poolName"
                    />

                    <!-- Prevents the previous image from flashing when showing the new one -->
                    <NuxtImg
                        v-show="false"
                        :src="
                            getPhotoUrlBySize(
                                photos[currentIndex - 1],
                                PhotoSizes.STANDARD
                            )
                        "
                    />
                    <NuxtImg
                        v-show="false"
                        :src="
                            getPhotoUrlBySize(
                                photos[currentIndex + 1],
                                PhotoSizes.STANDARD
                            )
                        "
                    />
                    <span class="w-full h-min absolute bottom-0" />
                    <div
                        v-if="currentPhoto?.comment"
                        class="bg-black opacity-80 w-full absolute bottom-0 p-2.5 text-white text-sm"
                    >
                        <p class="opacity-100 text-white">
                            {{ currentPhoto.comment }}
                        </p>
                    </div>
                </div>
                <IconChevronRight
                    class="cursor-pointer text-[#DDDDDD] absolute right-5 size-6"
                    @click="changePhoto('next')"
                />
            </div>
        </div>
    </div>
</template>
